import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { Add } from '@mui/icons-material';
import { Box, Chip, Skeleton, Tooltip, Typography } from '@mui/material';
import {
  GridRenderCellParams,
  GridValueFormatterParams,
  getGridSingleSelectOperators
} from '@mui/x-data-grid';

import { useQueryClient } from '@tanstack/react-query';

import Page from '@/components/Page';
import Button from '@/components/button/Button';
import DataTable from '@/components/dataTable';
import ChipsColumn from '@/components/dataTable/customChipsColumn';
import dateRangeOperator from '@/components/dataTable/customOperators/dateRangeOperator';
import Header from '@/components/header/Header';
import { creatorDetailsteps } from '@/constants/driverSteps';
import PLATFORM_MAPPER from '@/constants/platformMapper';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/constants/table';
import {
  useDeleteCreator,
  useFetchCreator,
  useFetchCreatorsPosts,
  useUpdateCreator
} from '@/hooks/queries/creators';
import { getPathWithLocalParams } from '@/hooks/urlParams';
import useAlert from '@/hooks/useAlert';
import useDriver from '@/hooks/useDriver';
import useTableParams from '@/hooks/useTableParams';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Creator } from '@/models/creator';
import { Labels } from '@/models/labels';
import { Platform } from '@/models/posts';
import { formatDate, getPrettyDate } from '@/utils/datesAndTime';
import getRoute from '@/utils/getRoute/getRoute';
import BooleanColumn from '@/views/evaluations/detail/body/_partials/BooleanColumn';
import PreviewColumn from '@/views/evaluations/detail/body/_partials/PreviewColumn';
import Error from '@/views/misc/error';

import DeleteDialog from '../delete/DeleteCreator';
import EditDialog from '../edit/EditCreator';
import Accounts from './_partials/Accounts';
import AddCreatorToGroups from './_partials/AddCreatorToGroups';
import MergeCreators from './_partials/MergeCreators';
import Profile from './_partials/Profile';

const defaultParams = {
  page: DEFAULT_PAGE,
  size: DEFAULT_PAGE_SIZE,
  platform: undefined,
  createTime: undefined
};

const CreatorDetail = () => {
  const { id: creatorId } = useParams();
  const navigate = useWorkspaceNavigate();
  const queryClient = useQueryClient();
  const alert = useAlert();
  const numericCreatorId = Number(creatorId);

  const { params, handlers, updateParams, paramsInTheUrl } = useTableParams(
    getRoute.creators.DETAIL(numericCreatorId, defaultParams),
    defaultParams
  );

  const getInitialFilterModelItems = (params: Record<string, any>) => {
    const { platform, createTime } = params;
    const items = [];

    if (platform !== undefined)
      items.push({
        field: 'platform',
        id: items.length,
        operator: 'isAnyOf',
        value: platform.split(',')
      });

    if (createTime !== undefined)
      items.push({
        field: 'uploadDate',
        id: items.length,
        operator: 'between',
        value: createTime.split(',').map((item: string) => new Date(item))
      });

    return items;
  };

  const initialFilterModel = useRef({
    items: getInitialFilterModelItems(params)
  });

  const onFilterModelChange = useCallback(
    (filterParams: Record<string, any>) => {
      const newParams: Record<string, any> = {};

      filterParams.items.forEach((item: { field: string; value: any }) => {
        const { field, value } = item;
        if (field === 'platform' && Array.isArray(value)) {
          const validPlatforms = value.filter(
            (v) => v && typeof v === 'string'
          );
          newParams.platform =
            validPlatforms.length > 0 ? validPlatforms.join(',') : undefined;
        }
        if (field === 'uploadDate') {
          const formattedDates: string[] = value
            ?.map((date: Date) => formatDate(date, 'yyyy-MM-dd'))
            .filter(Boolean);

          newParams.createTime = formattedDates?.length
            ? formattedDates.join(',')
            : undefined;
        }
      });
      [
        { field: 'platform', param: 'platform' },
        { field: 'uploadDate', param: 'createTime' }
      ].forEach(({ field, param }) => {
        const existedInUrl = Object.keys(paramsInTheUrl).includes(param);
        const stillActive = filterParams.items.some(
          (i: { field: string }) => i.field === field
        );
        if (existedInUrl && !stillActive) newParams[param] = undefined;
      });

      updateParams({ ...paramsInTheUrl, ...newParams, page: DEFAULT_PAGE });
    },
    [paramsInTheUrl, updateParams]
  );

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddToGroupsDialogOpen, setIsAddToGroupsDialogOpen] = useState(false);
  const [isMergeCreatorsDialogOpen, setIsMergeCreatorsDialogOpen] =
    useState(false);

  const {
    data: creator,
    isLoading: isLoadingCreator,
    error
  } = useFetchCreator({ id: numericCreatorId });

  const { data: creatorPosts, isFetching: isFetchingCreatorPosts } =
    useFetchCreatorsPosts({
      id: numericCreatorId,
      params: { ...params }
    });

  const { mutate: deleteCreator, isPending: isDeleting } = useDeleteCreator({
    onSuccess: () => {
      alert.success(`${creator?.name} was successfully deleted!`);
      queryClient.invalidateQueries({
        queryKey: ['posts'],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creators'],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['post'],
        refetchType: 'inactive'
      });

      navigate(getRoute.creators.LIST());
    },
    onError: () => alert.error(`Some error occurred while deleting the creator`)
  });

  const { mutate: updateCreator, isPending: isUpdating } = useUpdateCreator({
    id: numericCreatorId,
    onSuccess: () => {
      alert.success(`Creator successfully updated!`);
      queryClient.invalidateQueries({
        queryKey: ['creator', numericCreatorId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['posts'],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creatorAvatars', numericCreatorId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creatorsPosts', numericCreatorId],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ['creators'],
        refetchType: 'all'
      });
      setIsEditDialogOpen(false);
    },
    onError: ({ response }) => {
      alert.error(`${response.data.message}`);
    }
  });

  const handleClickDelete = () => {
    setIsEditDialogOpen(false);
    setIsDeleteDialogOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        sortable: false,
        disableColumnMenu: true,
        filterable: false,
        field: 'previewUrl',
        headerName: 'Preview',
        minWidth: 130,
        maxWidth: 130,
        flex: 1,
        renderCell: (params: GridRenderCellParams<Creator.Post>) => (
          <PreviewColumn
            previewUrl={params.row.previewUrl}
            isExpired={params.row.isExpired}
          />
        )
      },
      {
        field: 'title',
        headerName: 'Title',
        disableColumnMenu: true,
        filterable: false,
        width: 150,
        renderCell: (params: GridRenderCellParams<Creator.Post>) => (
          <Tooltip title={params.row.title || 'No title available'}>
            <Typography
              style={{
                maxHeight: '50px',
                textOverflow: 'ellipsis',
                overflow: 'hidden'
              }}
            >
              {params.row.title || <>—</>}
            </Typography>
          </Tooltip>
        )
      },
      {
        sortable: false,
        field: 'username',
        headerName: 'Account',
        disableColumnMenu: true,
        filterable: false,
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<Creator.Post>) => (
          <Tooltip title={params.row.username || 'No username available'}>
            <Typography
              style={{
                maxHeight: '50px',
                textOverflow: 'ellipsis',
                overflow: 'hidden'
              }}
            >
              {params.row.username || <>—</>}
            </Typography>
          </Tooltip>
        )
      },
      {
        sortable: false,
        field: 'platform',
        headerName: 'Platform',
        type: 'singleSelect',
        valueOptions: Object.keys(PLATFORM_MAPPER).map((key) => ({
          value: key,
          label: PLATFORM_MAPPER[key as Platform]
        })),
        filterOperators: getGridSingleSelectOperators().filter(
          (op) => op.value === 'isAnyOf'
        ),
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<Creator.Post>) => (
          <Chip label={PLATFORM_MAPPER[params.row.platform as Platform]} />
        )
      },
      {
        sortable: false,
        field: 'uploadDate',
        headerName: 'Publication Date',
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams<Creator.Post>) =>
          String(params.value),
        filterOperators: dateRangeOperator,
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<Creator.Post>) => (
          <Typography variant="body2">
            {getPrettyDate(params.row.uploadDate)}
          </Typography>
        )
      },
      {
        sortable: false,
        field: 'platformDisclosure',
        headerName: 'Sponsored',
        disableColumnMenu: true,
        filterable: false,
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<Creator.Post>) => (
          <BooleanColumn value={params.row.platformDisclosure} />
        )
      },
      {
        field: 'labels',
        headerName: 'Labels',
        filterable: false,
        minWidth: 130,
        flex: 1,
        sortable: false,
        valueFormatter: (params: GridValueFormatterParams<Labels.Details>) =>
          params.value.name || '—',
        renderCell: (data: GridRenderCellParams<Creator.Post>) => {
          const { labels } = data.row;
          return (
            <ChipsColumn
              data={labels.map((label) => ({
                keyword: label.name,
                tooltip: label.category?.name ?? ''
              }))}
              chipColor="default"
              cellWidth={data.colDef.computedWidth}
            />
          );
        }
      }
    ],
    [creatorPosts]
  );

  useDriver({
    isLoading: isLoadingCreator,
    steps: creatorDetailsteps
  });

  if (error)
    return (
      <Page>
        <Error message="Failed to get creator details" error={error} />
      </Page>
    );

  return (
    <Page
      title={creator?.name || 'Creator detail page'}
      header={
        <Header
          isSmallHeader
          actions={
            <Button
              id="creators-add-group"
              onClick={() => setIsAddToGroupsDialogOpen(true)}
              startIcon={<Add />}
              text="Add to group"
              size="small"
            />
          }
          breadcrumbsList={[
            {
              name: 'Creators',
              onClick: () =>
                navigate(getPathWithLocalParams(getRoute.creators.LIST()))
            }
          ]}
          onClickReturn={() =>
            navigate(getPathWithLocalParams(getRoute.creators.LIST()))
          }
          title={
            isLoadingCreator ? (
              <Skeleton variant="text" width="200px" />
            ) : (
              creator?.name
            )
          }
        />
      }
    >
      <>
        <Profile
          creator={creator}
          showSkeleton={isLoadingCreator}
          onClickEdit={() => setIsEditDialogOpen(true)}
          onClickMerge={() => setIsMergeCreatorsDialogOpen(true)}
        />
        <Accounts accounts={creator?.accounts} isLoading={isLoadingCreator} />
        <Box id="creators-posts-list">
          <DataTable
            rowHeight={85}
            rows={creatorPosts?.results || []}
            rowCount={creatorPosts?.totalItems}
            columns={columns}
            loading={isFetchingCreatorPosts}
            paginationMode="server"
            filterMode="server"
            initialFilterModel={initialFilterModel.current}
            onFilterModelChange={onFilterModelChange}
            onChangePageSize={handlers.onChangePageSize}
            onChangePage={handlers.onChangePage}
            page={params.page}
            size={params.size}
            onRowClick={(row) =>
              navigate(getRoute.creators.POST(numericCreatorId, row.id))
            }
          />
        </Box>
        {isEditDialogOpen && (
          <EditDialog
            creator={creator}
            isOpen={isEditDialogOpen}
            onClose={() => {
              queryClient.invalidateQueries({
                queryKey: ['creatorAvatars', numericCreatorId],
                refetchType: 'all'
              });
              setIsEditDialogOpen(false);
            }}
            onSubmit={(data) => updateCreator(data)}
            onClickDelete={handleClickDelete}
            isUpdating={isUpdating}
          />
        )}
        {isMergeCreatorsDialogOpen && creator && (
          <MergeCreators
            creator={creator}
            isOpen={isMergeCreatorsDialogOpen}
            onClose={() => setIsMergeCreatorsDialogOpen(false)}
          />
        )}
        {isAddToGroupsDialogOpen && (
          <AddCreatorToGroups
            isOpen={isAddToGroupsDialogOpen}
            onClose={() => setIsAddToGroupsDialogOpen(false)}
          />
        )}
        {isDeleteDialogOpen && creator && (
          <DeleteDialog
            name={creator?.name}
            isOpen={isDeleteDialogOpen}
            onClose={() => {
              setIsEditDialogOpen(true);
              setIsDeleteDialogOpen(false);
            }}
            onSubmit={() => deleteCreator({ ids: [numericCreatorId] })}
            isDeleting={isDeleting}
          />
        )}
      </>
    </Page>
  );
};

export default CreatorDetail;
